import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, compact } from 'lodash-es';
import classNames from 'classnames';
// Models
import { InventoryEntities } from 'client/data/models/inventory';
// Utils
import {
  getFallbackFeatures,
  getFeaturesByCategories,
} from 'client/site-modules/shared/utils/inventory/features-and-specs';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { getSortedOptionDescriptions } from 'site-modules/shared/utils/get-sorted-option-descriptions';
import { hideOptions } from 'site-modules/shared/utils/hide-options';
import { showFeaturesTab } from 'client/site-modules/shared/utils/inventory/show-features-tab';
import { showTrimName } from 'site-modules/shared/utils/show-trim-name';
// Components
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { FeaturesAndSpecsBase } from './features-and-specs-base';

const SCROLL_OFFSET = -70;
const MIN_HEIGHT_MOBILE = '304px';
const MIN_HEIGHT_DESKTOP = '280px';

/**
 * Component that represents all options, features and specs with
 * show/hide functionality.
 */
export function FeaturesAndSpecs({ vinData = {}, isMobile = false, collapseOnLoad = false, className = '' }) {
  const options = getSortedOptionDescriptions(vinData);

  let featuresByCategories = getFeaturesByCategories(vinData);
  const isEmptyFeatures = isEmpty(featuresByCategories);
  const isPartnerListing = isPartnerDealer(vinData);

  const upgrades = get(vinData, 'vehicleInfo.partsInfo.options', []);
  const showInstalledUpgrades = isPartnerListing && !hideOptions(vinData) && !!upgrades.length;
  const hideFeatures = !showFeaturesTab(vinData);

  if (hideFeatures) {
    return null;
  }

  if (options.length && isEmptyFeatures) {
    featuresByCategories = getFallbackFeatures(options);
  }

  const { make, model, year, trim } = get(vinData, 'vehicleInfo.styleInfo', {});
  const displayTrim = showTrimName(make, trim) ? trim : '';

  const collapseConfig = collapseOnLoad
    ? {
        minHeight: isMobile ? MIN_HEIGHT_MOBILE : MIN_HEIGHT_DESKTOP,
        scrollOffset: SCROLL_OFFSET,
        btnTrackingId: 'view_more_info',
      }
    : null;

  return (
    <VdpModule
      className={classNames('features-and-specs', 'bg-cool-gray-90', className)}
      headerTag="h2"
      sectionTitle="Features"
      titleClassName="heading-3"
      subCopy={`Features that are installed on this ${compact([year, make, model, displayTrim]).join(' ')}.`}
      collapseConfig={collapseConfig}
      contentCollapseClasses="mb-2_5"
    >
      <FeaturesAndSpecsBase
        vinData={vinData}
        isMobile={isMobile}
        hideFeatures={hideFeatures}
        featuresByCategories={featuresByCategories}
        showInstalledUpgrades={showInstalledUpgrades}
      />
    </VdpModule>
  );
}

FeaturesAndSpecs.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  collapseOnLoad: PropTypes.bool,
  className: PropTypes.string,
};
